import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { getCookieConsent, isAnalyticsAllowed, isMarketingAllowed } from './CookieConsent';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * TrackingScripts Component - Injects GA, GTM, and FB Pixel scripts
 * GDPR compliant: Only loads scripts when user has given consent
 */
const TrackingScripts = ({ consent }) => {
  const [loaded, setLoaded] = useState({
    analytics: false,
    marketing: false
  });
  const [trackingConfig, setTrackingConfig] = useState(null);

  // Load tracking configuration
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`${API}/tracking`);
        setTrackingConfig(response.data);
        window.__trackingConfig = response.data;
      } catch (error) {
        console.error('Failed to load tracking settings:', error);
      }
    };
    loadConfig();
  }, []);

  // Load analytics scripts (GA, GTM) - using safe script creation
  const loadAnalytics = useCallback(() => {
    if (loaded.analytics || !trackingConfig) return;
    
    // Sanitize tracking IDs to prevent XSS
    const sanitizeId = (id) => id ? id.replace(/[^a-zA-Z0-9-_]/g, '') : '';
    
    // Google Tag Manager is hardcoded in public/index.html (GTM-N5TG3655), not injected here
    
    // Google Analytics 4
    if (trackingConfig.tracking_ga_id) {
      const gaId = sanitizeId(trackingConfig.tracking_ga_id);
      const gaScript = document.createElement('script');
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(gaScript);
      
      const gaInit = document.createElement('script');
      gaInit.textContent = `
        gtag('js', new Date());
        gtag('config', '${gaId}', {
          'anonymize_ip': true
        });
      `;
      document.head.appendChild(gaInit);
      
      if (process.env.NODE_ENV === 'development') console.log('GA4 loaded with consent');
    }
    
    setLoaded(prev => ({ ...prev, analytics: true }));
  }, [trackingConfig, loaded.analytics]);

  // Load marketing scripts (FB Pixel, Google Ads) - using safe script creation
  const loadMarketing = useCallback(() => {
    if (loaded.marketing || !trackingConfig) return;
    
    // Sanitize pixel ID to prevent XSS
    const sanitizeId = (id) => id ? id.replace(/[^a-zA-Z0-9-_]/g, '') : '';
    
    // Facebook Pixel
    if (trackingConfig.tracking_fb_pixel_id) {
      const fbPixelId = sanitizeId(trackingConfig.tracking_fb_pixel_id);
      const fbScript = document.createElement('script');
      fbScript.textContent = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${fbPixelId}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(fbScript);
      
      const fbNoscript = document.createElement('noscript');
      const fbImg = document.createElement('img');
      fbImg.height = 1;
      fbImg.width = 1;
      fbImg.style.display = 'none';
      fbImg.src = `https://www.facebook.com/tr?id=${fbPixelId}&ev=PageView&noscript=1`;
      fbNoscript.appendChild(fbImg);
      document.body.appendChild(fbNoscript);
      
      if (process.env.NODE_ENV === 'development') console.log('FB Pixel loaded with consent');
    }
    
    // Google Ads conversion tracking is loaded via GA/GTM
    // We just need to ensure gtag is available for conversion events
    
    setLoaded(prev => ({ ...prev, marketing: true }));
  }, [trackingConfig, loaded.marketing]);

  // React to consent changes
  useEffect(() => {
    if (!trackingConfig) return;
    
    const currentConsent = consent || getCookieConsent();
    
    if (currentConsent?.analytics && !loaded.analytics) {
      loadAnalytics();
    }
    
    if (currentConsent?.marketing && !loaded.marketing) {
      loadMarketing();
    }
  }, [consent, trackingConfig, loaded, loadAnalytics, loadMarketing]);

  return null;
};

export const pushDataLayerEvent = (event, params = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
};

/**
 * Push GTM dataLayer event for a submitted lead. Waits for full page load.
 * Caller guarantees single invocation (conversion token is consumed).
 */
export const pushLeadSubmittedEvent = (leadMeta) => {
  const push = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'lead_submitted',
      form_type: leadMeta?.form_type || 'offerte_aanvraag',
      service_type: leadMeta?.service_type || '',
      situatie: leadMeta?.situatie || '',
      urgentie: leadMeta?.urgentie || '',
      property_type: leadMeta?.property_type || '',
      reason: leadMeta?.reason || '',
      timing: leadMeta?.timing || '',
      lead_id: leadMeta?.lead_id || '',
      conversion_value: leadMeta?.conversion_value || 0,
      currency: 'EUR'
    });
    if (process.env.NODE_ENV === 'development') console.log('dataLayer lead_submitted pushed');
  };
  if (document.readyState === 'complete') {
    push();
  } else {
    window.addEventListener('load', push, { once: true });
  }
};

/**
 * Fire conversion events - called on thank you page
 * Only fires if appropriate consent is given
 */
export const fireConversionEvents = (tracking) => {
  if (!tracking) return;
  
  const consent = getCookieConsent();
  
  // Google Analytics conversion event (requires analytics consent)
  if (consent?.analytics && tracking.tracking_ga_id && tracking.tracking_conversion_event && window.gtag) {
    window.gtag('event', tracking.tracking_conversion_event, {
      'event_category': 'lead',
      'event_label': 'form_submission',
      'value': tracking.tracking_conversion_value || 0
    });
    console.log('GA conversion event fired:', tracking.tracking_conversion_event);
  }
  
  // Google Ads conversion (requires marketing consent)
  if (consent?.marketing && tracking.tracking_gads_conversion_id && tracking.tracking_gads_conversion_label && window.gtag) {
    window.gtag('event', 'conversion', {
      'send_to': `${tracking.tracking_gads_conversion_id}/${tracking.tracking_gads_conversion_label}`,
      'value': tracking.tracking_conversion_value || 0,
      'currency': 'EUR'
    });
    console.log('Google Ads conversion fired');
  }
  
  // Facebook Pixel lead event (requires marketing consent)
  if (consent?.marketing && tracking.tracking_fb_pixel_id && window.fbq) {
    window.fbq('track', 'Lead', {
      value: tracking.tracking_conversion_value || 0,
      currency: 'EUR'
    });
    console.log('FB Pixel Lead event fired');
  }
};

export default TrackingScripts;
